import seedrandom from 'seedrandom';

import { types as sdkTypes } from './sdkLoader';
import locationPrivacyMath from './locationPrivacyMath';

const { LatLng } = sdkTypes;
const {
  DEFAULT_LOCATION_PRIVACY_RADIUS_METERS,
  LOCATION_PRIVACY_VERSION,
  createApproximateCoordinateLiteral,
  hasValidApproximateLocationContract,
  hasValidCoordinateLiteral,
} = locationPrivacyMath;

export { DEFAULT_LOCATION_PRIVACY_RADIUS_METERS, LOCATION_PRIVACY_VERSION };

const secureRandom = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0] / 2 ** 32;
  }
  return Math.random();
};

export const hasValidLocationCoordinates = hasValidCoordinateLiteral;

const coordinatesMatch = (first, second) =>
  hasValidLocationCoordinates(first) &&
  hasValidLocationCoordinates(second) &&
  Math.abs(first.lat - second.lat) < 1e-7 &&
  Math.abs(first.lng - second.lng) < 1e-7;

const privacyData = listing => listing?.attributes?.publicData?.locationPrivacy;

export const hasPersistedApproximateLocation = listing => {
  const attributes = listing?.attributes || {};
  return hasValidApproximateLocationContract({
    geolocation: attributes.geolocation,
    exactLocation: attributes.privateData?.exactLocation,
    privacy: privacyData(listing),
  });
};

/**
 * Move a coordinate away from the exact point while keeping it inside the privacy radius.
 */
export const createApproximateCoordinates = (
  exactCoordinates,
  radiusMeters = DEFAULT_LOCATION_PRIVACY_RADIUS_METERS,
  random = secureRandom
) => {
  const approximate = createApproximateCoordinateLiteral(exactCoordinates, radiusMeters, random);
  return new LatLng(approximate.lat, approximate.lng);
};

/**
 * Return the owner's exact editable location. Legacy public locations are accepted until saved
 * once, which migrates them to privateData.
 */
export const getEditableExactLocation = listing => {
  const attributes = listing?.attributes || {};
  const exactLocation = attributes.privateData?.exactLocation;
  const exactAddress =
    typeof exactLocation?.address === 'string' ? exactLocation.address.trim() : '';

  if (hasValidLocationCoordinates(exactLocation) && exactAddress) {
    return {
      address: exactAddress,
      building: exactLocation.building || '',
      origin: new LatLng(exactLocation.lat, exactLocation.lng),
    };
  }

  const legacyLocation = attributes.publicData?.location;
  const legacyAddress =
    typeof legacyLocation?.address === 'string' ? legacyLocation.address.trim() : '';
  if (
    !hasPersistedApproximateLocation(listing) &&
    hasValidLocationCoordinates(attributes.geolocation) &&
    legacyAddress
  ) {
    return {
      address: legacyAddress,
      building: legacyLocation.building || '',
      origin: attributes.geolocation,
    };
  }

  return null;
};

/**
 * Build a listing update that keeps the exact location private and exposes only an approximation.
 */
export const createPrivateLocationUpdate = ({
  listing,
  address,
  building = '',
  origin,
  radiusMeters = DEFAULT_LOCATION_PRIVACY_RADIUS_METERS,
  random = secureRandom,
}) => {
  if (typeof address !== 'string' || !address.trim() || !hasValidLocationCoordinates(origin)) {
    throw new Error('A selected address and coordinates are required.');
  }

  const existingExactLocation = getEditableExactLocation(listing);
  const existingRadius = Number(privacyData(listing)?.radiusMeters);
  const canReuseApproximation =
    hasPersistedApproximateLocation(listing) &&
    coordinatesMatch(existingExactLocation?.origin, origin) &&
    existingRadius === radiusMeters &&
    hasValidLocationCoordinates(listing?.attributes?.geolocation);

  const approximateCoordinates = canReuseApproximation
    ? listing.attributes.geolocation
    : createApproximateCoordinates(origin, radiusMeters, random);

  return {
    geolocation: approximateCoordinates,
    publicData: {
      location: null,
      locationPrivacy: {
        version: LOCATION_PRIVACY_VERSION,
        radiusMeters,
      },
    },
    privateData: {
      exactLocation: {
        address: address.trim(),
        building,
        lat: origin.lat,
        lng: origin.lng,
      },
    },
  };
};

export const createClearedPrivateLocationUpdate = () => ({
  geolocation: null,
  publicData: {
    location: null,
    locationPrivacy: null,
  },
  privateData: {
    exactLocation: null,
  },
});

/**
 * Return coordinates safe for public rendering. Persisted approximations are used as-is, while
 * legacy public coordinates receive a stable UI-only fallback until those listings are migrated.
 */
export const getPublicListingCoordinates = (
  listing,
  fallbackRadiusMeters = DEFAULT_LOCATION_PRIVACY_RADIUS_METERS
) => {
  const geolocation = listing?.attributes?.geolocation;
  if (!hasValidLocationCoordinates(geolocation)) {
    return null;
  }
  if (hasPersistedApproximateLocation(listing)) {
    return geolocation;
  }

  const id = listing?.id?.uuid || 'listing';
  const seed = `${id}_${geolocation.lat}_${geolocation.lng}`;
  return createApproximateCoordinates(geolocation, fallbackRadiusMeters, seedrandom(seed));
};
